import React, { useState } from 'react';
import parse from 'html-react-parser';
import { formConfig } from '@Utils/formConfig';

import LoadingSpinner from '../LoadingSpinner';
import { postHttp } from '@Utils/useHttp';
import { useGlobalState } from '@Store/auth.store';
import ButtonContainer from '../ButtonContainer';
import Grid from '../Grid';

export interface ConsentFormProps {
	baseUrl: string
	blockId: string
	confirmContent: string
	confirmTitle: string
	formContent: string
	formMailLabel: string
	formPhoneLabel: string
	formSamtykkeLabel: string
	infoFooter: string
	infoTekst: string
	knapTekst: string
	kontaktOplysningKilde?: string
	overskrift: string
	userInfo: {
		samtykkeLabel: string,
		email: string,
		samtykke: boolean,
		telefon: string
	}
	onSubmitSuccess?: () => void
}

const ConsentForm = ({
	overskrift,
	blockId,
	infoFooter,
	infoTekst,
	userInfo,
	formContent,
	formMailLabel,
	formPhoneLabel,
	knapTekst,
	confirmTitle,
	confirmContent,
	kontaktOplysningKilde = 'Selvbetjening',
	onSubmitSuccess
}: ConsentFormProps) => {

	const [showReceipt, setShowReceipt] = useState(false);
	const [errorEmail, setErrorEmail] = useState(false);
	const [errorPhn, setErrorPhn] = useState(false);
	const [fields, setFields] = useState({ telefon: userInfo?.telefon || '', email: userInfo?.email || '', samtykke: !!userInfo?.samtykke, blockId: blockId || '' });
	const [isLoading, setIsLoading] = useState(false);
	const [loggedInType] = useGlobalState('loggedInType');

	const onSubmit = async (e: { preventDefault: () => void; }) => {
		e.preventDefault();

		if (isFormValid()){
			if (loggedInType.toLowerCase() === 'mitid') {
				setIsLoading(true);
				try {
					const response = await postHttp('/Profile/GemKontaktInfo', { ...fields, kontaktOplysningKilde });

					if (response?.status) {
						if (onSubmitSuccess) {
							onSubmitSuccess();
						} else {
							setShowReceipt(true);
						}
					}
				} catch (error) {
					console.error(error);
				}
				setIsLoading(false);
			}
		}
	};

	const isFormValid = () => {
		let isValid = true;

		if (fields.telefon && !fields.telefon.match(formConfig.telephone)) {
			setErrorPhn(true);
			isValid = false;
		}

		if (fields.email && !fields.email.match(formConfig.emailDomain)) {
			setErrorEmail(true);
			isValid = false;
		}

		return isValid;
	};

	return (
		<div className="lhp-consent-form lhp-main">
			<Grid className="page" mobileBlock>
				{ showReceipt && (
					<Grid.Element className="lhp-consent-form__receipt" position="center">
						<div className="lhp-consent-form__receipt__header">
							<svg width="26" height="26" viewBox="0 0 26 26" fill="none" xmlns="http://www.w3.org/2000/svg">
								<circle cx="13" cy="13" r="13" fill="#92004D" />
								<path d="M8 12.875L11.9552 17L18.875 8" stroke="white" strokeWidth="2" />
							</svg>
							{ confirmTitle }
						</div>
						<div className="lhp-consent-form__receipt__body">
							{ confirmContent }
						</div>
					</Grid.Element>
				) }

				{ !showReceipt && (
					<>
						<Grid.Element className="lhp-consent-form__content" position="left" customStyle={ { gridColumn:'2/span 5' } }>
							{ overskrift && <h5>{ overskrift }</h5> }
							{ infoTekst && parse(infoTekst || '') }
						</Grid.Element>
						{ infoFooter && (
							<Grid.Element className="lhp-consent-form__footer" customStyle={ { gridColumn:'2/span 10', gridRow: '3', marginTop: '2rem' } } >
								{ parse(infoFooter || '') }
							</Grid.Element>
						) }
						<Grid.Element className="lhp-consent-form__form" position="right" customStyle={ { gridColumn:'8/span 4' } }>
							<form onSubmit={ onSubmit } className="lhp-form">
								{ formContent && <p>{ formContent }</p> }
								<fieldset >
									<label htmlFor="email"><span className="label-text">{ formMailLabel }</span>
										<input
											type="text"
											name="email"
											className={ errorEmail ? 'input-text error' : 'input-text' }
											value={ fields.email }
											onChange={ (e) => {
												setFields({ ...fields, email: e.target.value }); setErrorEmail(false);
											} }
										/>
									</label>
									<label htmlFor="phn"><span className="label-text">{ formPhoneLabel }</span>
										<input
											type="tel"
											name="phn"
											className={ errorPhn ? 'input-tel error' : 'input-tel' }
											defaultValue={ fields.telefon }
											onChange={ (e) => {
												setFields({ ...fields, telefon: e.target.value }); setErrorPhn(false);
											} }
										/>
									</label>

									<div className="consent-switch">
										<label
											htmlFor="switch"
											className="switch"
											onClick={ ()=>{
												setFields({ ...fields, samtykke: !fields.samtykke });
											} }>
											<input
												type="checkbox"
												name="switch"
												onChange={ ()=>{
													setFields({ ...fields, samtykke: !fields.samtykke });
												} }
												checked={ !!fields.samtykke } />
											<span className="toggle-switch" />
										</label>
										<p className="consent-toggle">{ userInfo?.samtykkeLabel }</p>
									</div>
								</fieldset>
								<ButtonContainer m_medium>
									<button className="btn btn-primary" type="submit" disabled={ !fields.samtykke || !(fields.telefon || fields.email) }>{ knapTekst }</button>
								</ButtonContainer>
								{ isLoading && (
									<LoadingSpinner />
								) }
							</form>
						</Grid.Element>
					</>
				) }
			</Grid>
		</div>
	);
};

export default ConsentForm;